import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Apollo, gql } from 'apollo-angular';
import { NotificationService } from 'src/app/shared/notification/notification.service';
import { GiftCardBalanceDetails } from '../interfaces/gift-card-balance-details';
import { DateTime } from 'luxon';
import { of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const query = gql`
    query Query(
        $customerID: Int!
        $giftCardID: String!
        $includeHistory: Boolean
    ) {
        giftCard(
            customerID: $customerID
            giftCardID: $giftCardID
            includeHistory: $includeHistory
        ) {
            id
            balance
            histories {
                action
                amount
                date
                endingBalance
            }
        }
    }
`;

@Component({
    selector: 'ols-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss'],
})
export class DetailsComponent implements OnInit, OnDestroy {
    public details: GiftCardBalanceDetails;
    public get transactionTableHeaders(): string[] {
        return ['date', 'action', 'amount'];
    }
    private ngUnsubscribe: Subject<any> = new Subject();

    constructor(
        private apollo: Apollo,
        private notificationService: NotificationService,
        private router: Router,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        const giftCardID: string = this.route.snapshot.paramMap.get('id');

        this.fetchDetails(giftCardID);
    }

    public goToCheckAnotherBalance() {
        this.router.navigate(['../'], { relativeTo: this.route });
    }

    public goToPurchaseGiftCardPage() {
        this.router.navigate(['../../'], { relativeTo: this.route });
    }

    private fetchDetails(giftCardID: string) {
        this.apollo
            .watchQuery({
                query,
                variables: {
                    giftCardID,
                    includeHistory: true,
                },
            })
            .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(
                (response: any) => {
                    if (response.data.giftCard) {
                        this.details = this.convertQueryResponseToDetails(
                            response.data.giftCard
                        );
                    } else if (response && response.errors) {
                        this.notificationService.showFailure(response.errors);
                        this.goToCheckAnotherBalance();
                    }
                },
                (err) => {
                    this.notificationService.showFailure(err);
                    this.goToCheckAnotherBalance();
                    return of(null);
                }
            );
    }

    private convertQueryResponseToDetails(
        giftCard: any
    ): GiftCardBalanceDetails {
        const details: GiftCardBalanceDetails = {
            id: giftCard.id,
            balance: giftCard.balance,
            transactionList: [],
        };

        giftCard.histories?.forEach((historyItem) => {
            details.transactionList.push({
                action: historyItem.action,
                amount: historyItem.amount,
                date: DateTime.fromISO(historyItem.date).toFormat('D'),
            });
        });

        return details;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
